import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  fetchMySeasonPass, claimSeasonPassTier, claimDailySeasonXp,
  fetchSeasonLeaderboard,
} from "../lib/api";
import { useUserHub } from "../hooks/useUserHub";
import { AvatarChip } from "./AvatarChip";
import { SeasonPassBanner } from "./SeasonPassBanner";

/**
 * SeasonPassPage — at `/season-pass`. Full tier track + XP earning rules
 * + claim modal + top XP leaderboard. When the platform flag is OFF, this
 * page renders a friendly "Season Pass is not currently active" state.
 */
export default function SeasonPassPage({ onToast }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimModal, setClaimModal] = useState(null); // { tier, track, reward }
  const [recentXp, setRecentXp] = useState(null);

  const reload = async () => {
    try {
      const s = await fetchMySeasonPass();
      setState(s);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load Season Pass.");
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line
  useEffect(() => { fetchSeasonLeaderboard(20).then((r) => setLeaders(r.rows || [])).catch(() => {}); }, []);

  // Daily login XP — fire once per page mount when signed in (server is idempotent).
  useEffect(() => {
    if (!user) return;
    claimDailySeasonXp().then((r) => {
      if (r.awarded) {
        onToast?.("🎁 Daily login XP claimed!");
        reload();
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [user?.user_id]);

  // Real-time XP toasts via the per-user hub.
  useUserHub((evt) => {
    if (!evt?.type) return;
    if (evt.type === "season_pass:xp") {
      setRecentXp({ amount: evt.amount, reason: evt.reason, at: Date.now() });
      // Only refresh full state on tier-up to avoid layout thrash.
      if (evt.new_tier > evt.prev_tier) {
        onToast?.(`🎉 Tier ${evt.new_tier} unlocked!`);
        reload();
      } else {
        setState((s) => s ? ({ ...s, xp: evt.xp || s.xp }) : s);
      }
    } else if (evt.type === "season_pass:claim") {
      reload();
    }
  });

  const openClaim = (tier, track) => {
    const reward = track === "free" ? tier.free_reward : tier.pro_reward;
    setClaimModal({ tier_num: tier.tier, track, reward });
  };

  const confirmClaim = async () => {
    if (!claimModal) return;
    setClaimBusy(true);
    try {
      const r = await claimSeasonPassTier(claimModal.tier_num, claimModal.track);
      setState(r.state);
      onToast?.(`🎉 Claimed: ${r.claim?.reward || "reward"}`);
      setClaimModal(null);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't claim reward.");
    } finally { setClaimBusy(false); }
  };

  const shareToX = (reward) => {
    const text = encodeURIComponent(`I just unlocked "${reward}" on the LEAGUE HUB Season Pass ⚡`);
    const url = encodeURIComponent(window.location.origin + "/season-pass");
    window.open(`https://x.com/intent/tweet?text=${text}&url=${url}`, "_blank", "noopener");
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="season-pass-signin">
        <h1 className="font-display text-4xl text-[#14b8a6]">SEASON PASS</h1>
        <p className="mt-3 text-white/70">Sign in to track your XP and claim tier rewards.</p>
        <Link to="/" className="inline-block mt-6 px-5 py-2.5 rounded-xl btn-neon text-[11px] tracking-widest">
          GO HOME · SIGN IN
        </Link>
      </div>
    );
  }
  if (!state) return <div className="p-8 text-white/50 text-sm">Loading…</div>;

  if (!state.enabled) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="season-pass-disabled">
        <h1 className="font-display text-4xl text-[#14b8a6]">SEASON PASS</h1>
        <p className="mt-3 text-white/70">
          The Season Pass is not currently active. Check back during the next
          season for XP earning & tier rewards.
        </p>
        <Link to="/dashboard" className="inline-block mt-6 px-5 py-2.5 rounded-xl btn-ghost text-[11px] tracking-widest">
          ← BACK TO DASHBOARD
        </Link>
      </div>
    );
  }

  const xp = state.xp || 0;
  const nextXp = state.next_tier_xp;
  const prevXp = state.prev_tier_xp || 0;
  const progressPct = nextXp
    ? Math.min(100, Math.max(0, ((xp - prevXp) / (nextXp - prevXp)) * 100))
    : 100;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="season-pass-page">
      <SeasonPassBanner />

      {/* Hero — XP / tier / progress */}
      <header className="rounded-3xl p-6 border-2 border-[#14b8a6]/40 bg-gradient-to-br from-[#1b073a] via-[#2a0a48] to-[#1b073a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle at 15% 30%, rgba(20, 184, 166,0.5), transparent 60%), radial-gradient(circle at 85% 70%, rgba(20, 184, 166,0.4), transparent 60%)" }} />
        <div className="relative flex items-center gap-6 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">⚡ SEASON PASS · LIVE</p>
            <h1 className="font-display text-4xl text-white mt-1" data-testid="season-pass-xp-total">
              {xp.toLocaleString()} <span className="text-[#14b8a6] text-2xl">XP</span>
            </h1>
            <p className="mt-2 text-sm text-white/80">
              You're at <span className="text-[#14b8a6] font-bold" data-testid="season-pass-current-tier">Tier {state.current_tier}</span>
              {nextXp ? <> — <span className="text-[#ffe066]">{(nextXp - xp).toLocaleString()}</span> XP to Tier {state.current_tier + 1}.</> : <> — <span className="text-[#14b8a6]">Max tier reached!</span></>}
            </p>
            <div className="mt-3 h-2.5 rounded-full bg-black/60 border border-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#14b8a6] to-[#14b8a6] transition-all"
                   style={{ width: `${progressPct}%` }}
                   data-testid="season-pass-progress-bar" />
            </div>
          </div>
          {state.is_pro ? (
            <span className="text-[10px] tracking-widest px-3 py-1.5 rounded-full bg-[#ffe066]/15 border border-[#ffe066]/50 text-[#ffe066] font-bold">
              <i className="fas fa-crown mr-1" />PRO TRACK UNLOCKED
            </span>
          ) : (
            <Link to="/pricing"
                  className="text-[11px] tracking-widest px-4 py-2 rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#14b8a6] text-black font-bold hover:brightness-110">
              UPGRADE TO PRO · $1.99/mo
            </Link>
          )}
        </div>
        {recentXp && Date.now() - recentXp.at < 4000 && (
          <div data-testid="season-pass-xp-popup"
               className="absolute top-4 right-4 px-3 py-1.5 rounded-xl bg-[#14b8a6]/20 border border-[#14b8a6]/50 text-[#14b8a6] text-[12px] tracking-widest font-bold animate-pulse">
            +{recentXp.amount} XP
          </div>
        )}
      </header>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Tier track */}
        <section className="space-y-3" data-testid="season-pass-tier-list">
          <p className="text-[10px] tracking-[4px] text-[#a3a3ac] font-bold">REWARD TRACK</p>
          {state.tiers.map((t) => (
            <TierRow key={t.tier} tier={t} isPro={state.is_pro} onClaim={openClaim} />
          ))}
        </section>

        {/* Right rail — XP rules + leaderboard */}
        <aside className="space-y-5">
          <XpRulesCard rules={state.xp_rules} />
          <LeaderboardCard rows={leaders} meId={user.user_id} />
        </aside>
      </div>

      {claimModal && (
        <ClaimModal
          payload={claimModal}
          busy={claimBusy}
          onConfirm={confirmClaim}
          onClose={() => setClaimModal(null)}
          onShare={shareToX}
        />
      )}
    </div>
  );
}

function TierRow({ tier, isPro, onClaim }) {
  const unlocked = tier.unlocked;
  const milestoneCls = tier.is_milestone
    ? "border-[#ffe066]/50 bg-gradient-to-r from-[#1a1505] to-[#1f0a1f]"
    : "border-white/10 bg-black/30";
  return (
    <div data-testid={`tier-row-${tier.tier}`}
         className={`rounded-2xl border ${milestoneCls} p-4 grid grid-cols-1 md:grid-cols-[60px_1fr_1fr_auto] gap-3 items-center transition`}>
      <div className="text-center">
        <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center font-display text-lg ${
          unlocked
            ? "bg-gradient-to-br from-[#14b8a6] to-[#14b8a6] text-black"
            : "bg-black/60 text-white/40 border border-white/15"
        }`}>
          {tier.tier}
        </div>
        {tier.is_milestone && <p className="text-[9px] tracking-[2px] text-[#ffe066] mt-1">MILESTONE</p>}
      </div>

      {/* Free reward */}
      <RewardCell
        label="FREE"
        reward={tier.free_reward}
        unlocked={unlocked}
        claimed={tier.claimed_free}
        xpRequired={tier.xp_required}
        accent="text-[#14b8a6]"
        onClaim={() => onClaim(tier, "free")}
        testId={`tier-${tier.tier}-free`}
        canClaim={unlocked && !tier.claimed_free}
      />

      {/* Pro reward */}
      <RewardCell
        label="PRO"
        reward={tier.pro_reward}
        unlocked={unlocked && isPro}
        claimed={tier.claimed_pro}
        xpRequired={tier.xp_required}
        accent="text-[#ffe066]"
        locked={!isPro}
        onClaim={() => onClaim(tier, "pro")}
        testId={`tier-${tier.tier}-pro`}
        canClaim={unlocked && tier.can_claim_pro && !tier.claimed_pro}
      />

      <div className="text-right md:text-center">
        <p className="text-[10px] tracking-widest text-white/45">REQUIRES</p>
        <p className="font-mono text-sm text-[#a3a3ac]">{tier.xp_required.toLocaleString()} XP</p>
      </div>
    </div>
  );
}

function RewardCell({ label, reward, unlocked, claimed, accent, locked, onClaim, testId, canClaim }) {
  return (
    <div className={`rounded-xl p-3 border ${unlocked ? "border-white/20 bg-black/30" : "border-white/5 bg-black/20"}`}>
      <p className={`text-[9px] tracking-[3px] font-bold ${accent}`}>{label}{locked ? " · PRO LOCK" : ""}</p>
      <p className={`text-sm mt-0.5 ${unlocked ? "text-white" : "text-white/40"}`}>{reward || "—"}</p>
      {claimed ? (
        <span data-testid={`${testId}-claimed`}
              className="inline-block mt-2 text-[10px] tracking-widest px-2 py-0.5 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40">
          <i className="fas fa-check mr-1" />CLAIMED
        </span>
      ) : canClaim ? (
        <button type="button" onClick={onClaim}
                data-testid={`${testId}-claim-btn`}
                className="mt-2 text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-neon">
          CLAIM
        </button>
      ) : (
        <span className="inline-block mt-2 text-[10px] tracking-widest px-2 py-0.5 rounded-md bg-white/5 text-white/40 border border-white/10">
          {locked ? "PRO REQUIRED" : "LOCKED"}
        </span>
      )}
    </div>
  );
}

function XpRulesCard({ rules }) {
  const rows = useMemo(() => [
    { icon: "fa-trophy", label: "Match won", v: rules?.match_won, color: "text-[#ffe066]" },
    { icon: "fa-flag-checkered", label: "Match played", v: rules?.match_played, color: "text-[#14b8a6]" },
    { icon: "fa-message", label: "Chat message", v: rules?.chat_message, sub: `daily cap ${rules?.chat_daily_cap || 50}`, color: "text-[#a3a3ac]" },
    { icon: "fa-gavel", label: "Open dispute", v: rules?.dispute_action, color: "text-[#ff8a3a]" },
    { icon: "fa-user-check", label: "Onboarding", v: rules?.onboarding_done, sub: "one-time", color: "text-[#14b8a6]" },
    { icon: "fa-calendar-day", label: "Daily login", v: rules?.daily_login, sub: "once / 24h", color: "text-[#14b8a6]" },
  ], [rules]);
  return (
    <div className="rounded-2xl p-4 bg-black/40 border border-white/10" data-testid="season-pass-rules">
      <p className="text-[10px] tracking-[3px] text-[#a3a3ac] font-bold mb-3">⚡ HOW TO EARN XP</p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center gap-3 text-sm">
            <i className={`fas ${r.icon} ${r.color} w-4`} />
            <span className="flex-1 text-white/85">{r.label}</span>
            <span className={`font-mono font-bold ${r.color}`}>+{r.v || 0}</span>
            {r.sub && <span className="text-[10px] text-white/40 ml-1">({r.sub})</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LeaderboardCard({ rows, meId }) {
  if (!rows.length) return (
    <div className="rounded-2xl p-4 bg-black/40 border border-white/10" data-testid="season-pass-leaderboard-empty">
      <p className="text-[10px] tracking-[3px] text-[#ffe066] font-bold">🏆 TOP XP EARNERS</p>
      <p className="text-[12px] text-white/45 mt-2">No XP yet this season — be the first.</p>
    </div>
  );
  return (
    <div className="rounded-2xl p-4 bg-black/40 border border-white/10" data-testid="season-pass-leaderboard">
      <p className="text-[10px] tracking-[3px] text-[#ffe066] font-bold mb-3">🏆 TOP XP EARNERS</p>
      <ol className="space-y-2">
        {rows.map((row, i) => (
          <li key={row.user_id}
              data-testid={`leaderboard-row-${i}`}
              className={`flex items-center gap-3 text-sm rounded-lg px-2 py-1.5 ${row.user_id === meId ? "bg-[#14b8a6]/20 border border-[#14b8a6]/40" : ""}`}>
            <span className={`font-mono text-xs w-5 text-right ${i < 3 ? "text-[#ffe066] font-bold" : "text-white/40"}`}>
              {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`}
            </span>
            <AvatarChip avatar_id={row.avatar_id} pfp_url={row.pfp_url} size={24} />
            <Link to={`/players/${row.user_id}`} className="flex-1 truncate text-white/85 hover:text-white">
              {row.name}
            </Link>
            <span className="font-mono text-xs text-[#14b8a6]">{row.xp.toLocaleString()}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ClaimModal({ payload, busy, onConfirm, onClose, onShare }) {
  const isPro = payload.track === "pro";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="claim-modal">
      <div onClick={(e) => e.stopPropagation()}
           className={`relative max-w-md w-full rounded-3xl p-6 border-2 ${isPro ? "border-[#ffe066]/60" : "border-[#14b8a6]/60"} bg-gradient-to-br from-[#0d0521] via-[#1b073a] to-[#0d0521] text-center overflow-hidden`}>
        {/* Confetti dots */}
        <div className="absolute inset-0 pointer-events-none opacity-50"
             style={{ background: "radial-gradient(circle at 30% 20%, rgba(20, 184, 166,0.4), transparent 60%), radial-gradient(circle at 80% 80%, rgba(20, 184, 166,0.4), transparent 60%)" }} />
        <div className="relative">
          <p className="text-[10px] tracking-[4px] text-white/60 font-bold">SEASON PASS · TIER {payload.tier_num}</p>
          <p className={`text-[10px] tracking-[3px] mt-1 font-bold ${isPro ? "text-[#ffe066]" : "text-[#14b8a6]"}`}>
            {isPro ? "👑 PRO REWARD" : "🟢 FREE REWARD"}
          </p>
          <h2 className="font-display text-3xl text-white mt-3" data-testid="claim-modal-reward">{payload.reward}</h2>
          <p className="text-[12px] text-white/55 mt-2">Confirm to add this reward to your profile flair.</p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <button type="button" onClick={onConfirm} disabled={busy}
                    data-testid="claim-modal-confirm-btn"
                    className={`text-[11px] tracking-widest px-5 py-2 rounded-xl font-bold disabled:opacity-50 ${
                      isPro ? "bg-gradient-to-r from-[#ffe066] to-[#ff8a3a] text-black" : "btn-neon"
                    }`}>
              {busy ? "…" : <><i className="fas fa-gift mr-1" />CLAIM REWARD</>}
            </button>
            <button type="button" onClick={() => onShare(payload.reward)}
                    data-testid="claim-modal-share-btn"
                    className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">
              <i className="fab fa-x-twitter mr-1" />SHARE
            </button>
            <button type="button" onClick={onClose}
                    data-testid="claim-modal-close-btn"
                    className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">
              CLOSE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
